// src/components/teacher/TeacherOverview.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL, getAuthHeaders } from "../../utils/api";
import {
  StudentsIcon,
  CalendarIcon,
  GradeIcon,
  ReportIcon,
  BookIcon,
  UsersIcon,
} from "../shared/icons/Icons";
import {
  USE_DUMMY_DATA,
  DUMMY_STATS,
  DUMMY_ACTIVITIES,
} from "./dummyData";

function TeacherOverview({ teacherData, courses, loading }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalStudents: USE_DUMMY_DATA ? DUMMY_STATS.total_students : 0,
    totalCourses: USE_DUMMY_DATA
      ? DUMMY_STATS.total_courses
      : courses?.length || 0,
    newStudents: USE_DUMMY_DATA ? DUMMY_STATS.new_students : 0,
    withdrawnStudents: USE_DUMMY_DATA ? DUMMY_STATS.withdrawn_students : 0,
    pendingGrades: USE_DUMMY_DATA ? DUMMY_STATS.pending_grades : 0,
  });
  const [recentActivities, setRecentActivities] = useState(
    USE_DUMMY_DATA ? DUMMY_ACTIVITIES : [],
  );
  const [loadingStats, setLoadingStats] = useState(!USE_DUMMY_DATA);

  useEffect(() => {
    if (USE_DUMMY_DATA) {
      setLoadingStats(false);
      return;
    }

    const fetchStats = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/teachers/me/stats`, {
          headers: {
            ...getAuthHeaders(),
            "Content-Type": "application/json",
          },
        });

        if (response.ok) {
          const data = await response.json();
          setStats({
            totalStudents: data.total_students || 0,
            totalCourses: data.total_courses || courses?.length || 0,
            newStudents: data.new_students || 0,
            withdrawnStudents: data.withdrawn_students || 0,
            pendingGrades: data.pending_grades || 0,
          });
        }
      } catch (err) {
        console.error("Failed to fetch stats:", err);
      } finally {
        setLoadingStats(false);
      }
    };

    const fetchActivities = async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/teachers/me/activities`,
          {
            headers: {
              ...getAuthHeaders(),
              "Content-Type": "application/json",
            },
          },
        );

        if (response.ok) {
          const data = await response.json();
          setRecentActivities(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        console.error("Failed to fetch activities:", err);
      }
    };

    fetchStats();
    fetchActivities();
  }, [courses]);

  if (loading || loadingStats) {
    return (
      <div className="tp-loading">
        <div className="tp-spinner" />
        <p>Loading dashboard...</p>
      </div>
    );
  }

  const statCards = [
    {
      title: "Total Students",
      value: stats.totalStudents,
      icon: StudentsIcon,
      color: "#1d4ed8",
      bgColor: "rgba(29, 78, 216, 0.1)",
      change: stats.newStudents > 0 ? `+${stats.newStudents} new` : "No change",
      to: "/teacher/students",
    },
    {
      title: "Total Courses",
      value: stats.totalCourses,
      icon: BookIcon,
      color: "#16a34a",
      bgColor: "rgba(22, 163, 74, 0.1)",
      change: "Active",
      to: "/teacher/timetable",
    },
    {
      title: "New This Month",
      value: stats.newStudents,
      icon: UsersIcon,
      color: "#7c3aed",
      bgColor: "rgba(124, 58, 237, 0.1)",
      change: "New enrollments",
      to: "/teacher/students",
    },
    {
      title: "Withdrawn",
      value: stats.withdrawnStudents,
      icon: ReportIcon,
      color: "#dc2626",
      bgColor: "rgba(220, 38, 38, 0.1)",
      change: "This month",
      to: "/teacher/students",
    },
    {
      title: "Pending Grades",
      value: stats.pendingGrades,
      icon: GradeIcon,
      color: "#d97706",
      bgColor: "rgba(217, 119, 6, 0.1)",
      change: "Needs attention",
      to: "/teacher/grades",
    },
  ];

  const quickActions = [
    {
      label: "Take Attendance",
      icon: CalendarIcon,
      to: "/teacher/attendance",
    },
    { label: "Enter Grades", icon: GradeIcon, to: "/teacher/grades" },
    { label: "View Students", icon: StudentsIcon, to: "/teacher/students" },
    { label: "My Timetable", icon: CalendarIcon, to: "/teacher/timetable" },
  ];

  return (
    <div>
      <div className="tp-page-header">
        <div>
          <h2>
            Welcome back, {teacherData?.name?.split(" ")[0] || "Teacher"}!
          </h2>
          <p>Here's what's happening with your classes today.</p>
        </div>
        <span className="tp-badge">
          {new Date().toLocaleDateString("en-US", {
            weekday: "long",
            month: "long",
            day: "numeric",
            year: "numeric",
          })}
        </span>
      </div>

      <div className="tp-stats-grid">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <button
              key={stat.title}
              type="button"
              className="tp-card tp-card--clickable"
              onClick={() => navigate(stat.to)}
            >
              <div className="tp-stat-top">
                <div
                  className="tp-stat-icon"
                  style={{ background: stat.bgColor, color: stat.color }}
                >
                  <Icon />
                </div>
                <span className="tp-badge">{stat.change}</span>
              </div>
              <h3 className="tp-stat-value">{stat.value}</h3>
              <p className="tp-stat-label">{stat.title}</p>
            </button>
          );
        })}
      </div>

      <h3 className="tp-section-title">Quick Actions</h3>
      <div className="tp-actions-grid">
        {quickActions.map((action) => {
          const Icon = action.icon;
          return (
            <button
              key={action.label}
              type="button"
              className="tp-action-btn"
              onClick={() => navigate(action.to)}
            >
              <Icon />
              <span>{action.label}</span>
            </button>
          );
        })}
      </div>

      {recentActivities.length > 0 ? (
        <>
          <h3 className="tp-section-title">Recent Activities</h3>
          <div className="tp-activity-list">
            {recentActivities.map((activity, index) => (
              <div key={`${activity.description}-${index}`} className="tp-activity-item">
                <div className="tp-activity-icon">
                  {activity.type === "grade" && <GradeIcon />}
                  {activity.type === "student" && <StudentsIcon />}
                  {activity.type === "class" && <CalendarIcon />}
                </div>
                <div className="tp-activity-body">
                  <p>{activity.description}</p>
                  <span>{activity.time}</span>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}

export default TeacherOverview;
